import { useState, useCallback, useEffect } from 'react';

import type { MoeveUserPayload } from '@/interfaces';
import { t } from 'i18next';

export interface UseMoeveUserFormProps {
  initialState?: Partial<MoeveUserPayload>;
  onSubmit: (payload: MoeveUserPayload) => Promise<void> | void;
}

export const useMoeveUserForm = ({ initialState, onSubmit }: UseMoeveUserFormProps) => {
  const defaultInitialState: MoeveUserPayload = {
    role: 'MOEVE_ADMIN',
    company: null,
  };

  const [form, setForm] = useState<MoeveUserPayload>(() => ({
    ...defaultInitialState,
    ...initialState,
  }));

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Partial<Record<keyof MoeveUserPayload, string>>>({});

  const updateField = useCallback(
    <K extends keyof MoeveUserPayload>(field: K, value: MoeveUserPayload[K]) => {
      setForm((prev) => ({ ...prev, [field]: value }));

      if (errors[field]) {
        setErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors[field];
          return newErrors;
        });
      }
    },
    [errors]
  );

  const validateForm = useCallback((): boolean => {
    const newErrors: Partial<Record<keyof MoeveUserPayload, string>> = {};

    if (!form.role) {
      newErrors.role = t('settings.moeveActionsTab.moeveUserModal.errorRole');
    }

    if (form.role !== 'MOEVE_ADMIN' && !form.company) {
      newErrors.company = t('settings.moeveActionsTab.moeveUserModal.errorCompany');
    }

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  }, [form]);

  const handleSubmit = useCallback(async () => {
    if (!validateForm()) return;

    setIsSubmitting(true);

    await onSubmit(form);

    setIsSubmitting(false);
  }, [form, onSubmit, validateForm]);

  useEffect(() => {
    if (form.role === 'MOEVE_ADMIN') {
      updateField('company', null);
    }
  }, [form.role, updateField]);

  return {
    form,
    isSubmitting,
    errors,
    updateRole: (value: string) => updateField('role', value as MoeveUserPayload['role']),
    updateCompany: (value: { id: number; value: string; label: string }) => updateField('company', value),
    handleSubmit,
  };
};
